import React from 'react';
import { Link, Outlet } from 'react-router-dom';
import { Menu, X, ChevronDown, Wind, Wrench, Zap, Home, Hammer, Building, Heart, GraduationCap, ShoppingBag, Factory, Shield, Hotel, Church } from 'lucide-react';
import { Menu as HeadlessMenu, Transition } from '@headlessui/react';

const PublicLayout: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <img
                  src="/WorkOrderHQLOGO.png"
                  alt="WorkOrder HQ"
                  className="h-10"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="/#platform" className="text-slate-700 transition-colors hover:text-blue-700">Product</a>
              <a href="/#platform" className="text-slate-700 transition-colors hover:text-blue-700">How it works</a>
              {/* Industries Dropdown */}
              <HeadlessMenu as="div" className="relative">
                <HeadlessMenu.Button className="flex items-center text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  Industries
                  <ChevronDown className="ml-1 h-4 w-4" />
                </HeadlessMenu.Button>
                <Transition
                  as={React.Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <HeadlessMenu.Items className="absolute left-0 mt-2 w-80 origin-top-left bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                    <div className="py-2">
                      <HeadlessMenu.Item>
                        {({ active }) => (
                          <Link
                            to="/industries/facilities"
                            className={`${
                              active ? 'bg-gray-50 dark:bg-gray-700' : ''
                            } flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400`}
                          >
                            <Building className="h-5 w-5 text-blue-600 mr-3" />
                            <div>
                              <div className="font-medium">Facilities Management</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Building maintenance & operations</div>
                            </div>
                          </Link>
                        )}
                      </HeadlessMenu.Item>
                      <HeadlessMenu.Item>
                        {({ active }) => (
                          <Link
                            to="/industries/healthcare"
                            className={`${
                              active ? 'bg-gray-50 dark:bg-gray-700' : ''
                            } flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400`}
                          >
                            <Heart className="h-5 w-5 text-red-600 mr-3" />
                            <div>
                              <div className="font-medium">Healthcare</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Hospitals, clinics & medical centers</div>
                            </div>
                          </Link>
                        )}
                      </HeadlessMenu.Item>
                      <HeadlessMenu.Item>
                        {({ active }) => (
                          <Link
                            to="/industries/education"
                            className={`${
                              active ? 'bg-gray-50 dark:bg-gray-700' : ''
                            } flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400`}
                          >
                            <GraduationCap className="h-5 w-5 text-green-600 mr-3" />
                            <div>
                              <div className="font-medium">Education</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Schools, universities & training centers</div>
                            </div>
                          </Link>
                        )}
                      </HeadlessMenu.Item>
                      <HeadlessMenu.Item>
                        {({ active }) => (
                          <Link
                            to="/industries/retail"
                            className={`${
                              active ? 'bg-gray-50 dark:bg-gray-700' : ''
                            } flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400`}
                          >
                            <ShoppingBag className="h-5 w-5 text-purple-600 mr-3" />
                            <div>
                              <div className="font-medium">Retail</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Stores, shopping centers & restaurants</div>
                            </div>
                          </Link>
                        )}
                      </HeadlessMenu.Item>
                      <HeadlessMenu.Item>
                        {({ active }) => (
                          <Link
                            to="/industries/manufacturing"
                            className={`${
                              active ? 'bg-gray-50 dark:bg-gray-700' : ''
                            } flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400`}
                          >
                            <Factory className="h-5 w-5 text-orange-600 mr-3" />
                            <div>
                              <div className="font-medium">Manufacturing</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Factories & production plants</div>
                            </div>
                          </Link>
                        )}
                      </HeadlessMenu.Item>
                      <HeadlessMenu.Item>
                        {({ active }) => (
                          <Link
                            to="/industries/government"
                            className={`${
                              active ? 'bg-gray-50 dark:bg-gray-700' : ''
                            } flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400`}
                          >
                            <Shield className="h-5 w-5 text-indigo-600 mr-3" />
                            <div>
                              <div className="font-medium">Government</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Municipalities & public services</div>
                            </div>
                          </Link>
                        )}
                      </HeadlessMenu.Item>
                      <HeadlessMenu.Item>
                        {({ active }) => (
                          <Link
                            to="/industries/hospitality"
                            className={`${
                              active ? 'bg-gray-50 dark:bg-gray-700' : ''
                            } flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400`}
                          >
                            <Hotel className="h-5 w-5 text-teal-600 mr-3" />
                            <div>
                              <div className="font-medium">Hospitality</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Hotels, resorts & event venues</div>
                            </div>
                          </Link>
                        )}
                      </HeadlessMenu.Item>
                      <HeadlessMenu.Item>
                        {({ active }) => (
                          <Link
                            to="/industries/property-management"
                            className={`${
                              active ? 'bg-gray-50 dark:bg-gray-700' : ''
                            } flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400`}
                          >
                            <Home className="h-5 w-5 text-purple-600 mr-3" />
                            <div>
                              <div className="font-medium">Property Management</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Apartments, condos & multi-unit properties</div>
                            </div>
                          </Link>
                        )}
                      </HeadlessMenu.Item>
                      <HeadlessMenu.Item>
                        {({ active }) => (
                          <Link
                            to="/industries/religious"
                            className={`${
                              active ? 'bg-gray-50 dark:bg-gray-700' : ''
                            } flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400`}
                          >
                            <Church className="h-5 w-5 text-yellow-600 mr-3" />
                            <div>
                              <div className="font-medium">Religious Organizations</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Churches, temples & religious centers</div>
                            </div>
                          </Link>
                        )}
                      </HeadlessMenu.Item>
                      <HeadlessMenu.Item>
                        {({ active }) => (
                          <Link
                            to="/industries/custom"
                            className={`${
                              active ? 'bg-gray-50 dark:bg-gray-700' : ''
                            } flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400`}
                          >
                            <Hammer className="h-5 w-5 text-gray-600 mr-3" />
                            <div>
                              <div className="font-medium">Custom</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Tailored solutions for specialized needs</div>
                            </div>
                          </Link>
                        )}
                      </HeadlessMenu.Item>
                    </div>
                  </HeadlessMenu.Items>
                </Transition>
              </HeadlessMenu>
              
              <a
                href="/pricing"
                className="text-slate-700 transition-colors hover:text-blue-700"
              >
                Pricing
              </a>
              <a
                href="/login"
                className="text-slate-700 transition-colors hover:text-blue-700"
              >
                Login
              </a>
              <a
                href="/signup"
                className="rounded bg-blue-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
              >
                Start Free Trial
              </a>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-700 dark:text-gray-300 hover:text-accent-color"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="px-4 py-3 space-y-3">
              {/* Industries Section */}
              <div className="border-b border-gray-200 dark:border-gray-700 pb-3 mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-2">
                  Industries
                </h3>
                <div className="space-y-2">
                  <Link
                    to="/industries/facilities"
                    className="flex items-center text-gray-700 dark:text-gray-300 hover:text-blue-600 py-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Building className="h-4 w-4 text-blue-600 mr-2" />
                    Facilities Management
                  </Link>
                  <Link
                    to="/industries/healthcare"
                    className="flex items-center text-gray-700 dark:text-gray-300 hover:text-blue-600 py-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Heart className="h-4 w-4 text-red-600 mr-2" />
                    Healthcare
                  </Link>
                  <Link
                    to="/industries/education"
                    className="flex items-center text-gray-700 dark:text-gray-300 hover:text-blue-600 py-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <GraduationCap className="h-4 w-4 text-green-600 mr-2" />
                    Education
                  </Link>
                  <Link
                    to="/industries/retail"
                    className="flex items-center text-gray-700 dark:text-gray-300 hover:text-blue-600 py-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <ShoppingBag className="h-4 w-4 text-purple-600 mr-2" />
                    Retail
                  </Link>
                  <Link
                    to="/industries/manufacturing"
                    className="flex items-center text-gray-700 dark:text-gray-300 hover:text-blue-600 py-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Factory className="h-4 w-4 text-orange-600 mr-2" />
                    Manufacturing
                  </Link>
                  <Link
                    to="/industries/government"
                    className="flex items-center text-gray-700 dark:text-gray-300 hover:text-blue-600 py-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Shield className="h-4 w-4 text-indigo-600 mr-2" />
                    Government
                  </Link>
                  <Link
                    to="/industries/hospitality"
                    className="flex items-center text-gray-700 dark:text-gray-300 hover:text-blue-600 py-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Hotel className="h-4 w-4 text-teal-600 mr-2" />
                    Hospitality
                  </Link>
                  <Link
                    to="/industries/property-management"
                    className="flex items-center text-gray-700 dark:text-gray-300 hover:text-blue-600 py-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Home className="h-4 w-4 text-purple-600 mr-2" />
                    Property Management
                  </Link>
                  <Link
                    to="/industries/religious"
                    className="flex items-center text-gray-700 dark:text-gray-300 hover:text-blue-600 py-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Church className="h-4 w-4 text-yellow-600 mr-2" />
                    Religious Organizations
                  </Link>
                  <Link
                    to="/industries/custom"
                    className="flex items-center text-gray-700 dark:text-gray-300 hover:text-blue-600 py-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Hammer className="h-4 w-4 text-gray-600 mr-2" />
                    Custom
                  </Link>
                </div>
              </div>
              
              <Link
                to="/pricing"
                className="block text-gray-700 dark:text-gray-300 hover:text-blue-600 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </Link>
              <Link
                to="/login"
                className="block text-gray-700 dark:text-gray-300 hover:text-blue-600 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Login
              </Link>
              <Link
                to="/signup"
                className="block bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-center font-semibold"
                onClick={() => setMobileMenuOpen(false)}
              >
                Start Free Trial
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
                Company
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-gray-600 dark:text-gray-400 hover:text-blue-600">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/pricing" className="text-gray-600 dark:text-gray-400 hover:text-blue-600">
                    Pricing
                  </Link>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
                Support
              </h3>
              <ul className="space-y-2">
                <li>
                  <a href="mailto:support@workorderhq.com" className="text-gray-600 dark:text-gray-400 hover:text-blue-600">
                    Contact Us
                  </a>
                </li>
                <li>
                  <a href="mailto:help@workorderhq.com" className="text-gray-600 dark:text-gray-400 hover:text-blue-600">
                    Help Center
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
                Legal
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/terms" className="text-gray-600 dark:text-gray-400 hover:text-blue-600">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-600 dark:text-gray-400 hover:text-blue-600">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>

            {/* Social */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
                Follow Us
              </h3>
              <ul className="space-y-2">
                <li>
                  <a href="#" className="text-gray-600 dark:text-gray-400 hover:text-blue-600">
                    Twitter
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-600 dark:text-gray-400 hover:text-blue-600">
                    LinkedIn
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
            <p className="text-center text-gray-500 dark:text-gray-400 text-sm">
              © {new Date().getFullYear()} WorkOrder HQ™. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
